// src/pages/ReportBuilderPage.js
import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

export default function ReportBuilderPage() {
  const [entities, setEntities] = useState(null);
  const [entityKey, setEntityKey] = useState('members');
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [filterValues, setFilterValues] = useState({});
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api.get('/report-builder/entities')
      .then(res => {
        setEntities(res.data);
        // Pre-select a few sensible default columns for the initial entity
        const defaultCols = (res.data[entityKey]?.columns || []).slice(0, 5).map(c => c.key);
        setSelectedColumns(defaultCols);
      })
      .catch(() => toast.error('Failed to load report builder.'));
  }, []);

  const currentEntity = entities?.[entityKey];

  const switchEntity = (newKey) => {
    setEntityKey(newKey);
    setFilterValues({});
    setPreview(null);
    const defaultCols = (entities[newKey]?.columns || []).slice(0, 5).map(c => c.key);
    setSelectedColumns(defaultCols);
  };

  const toggleColumn = (key) => {
    setSelectedColumns(cols => cols.includes(key) ? cols.filter(c => c !== key) : [...cols, key]);
    setPreview(null);
  };

  const selectAll = () => setSelectedColumns((currentEntity?.columns || []).map(c => c.key));
  const selectNone = () => setSelectedColumns([]);

  const setFilter = (key, value) => {
    setFilterValues(f => ({ ...f, [key]: value }));
    setPreview(null);
  };

  const handlePreview = async () => {
    if (selectedColumns.length === 0) {
      toast.error('Pick at least one column.');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/report-builder/preview', {
        entity: entityKey,
        columns: selectedColumns,
        filters: cleanFilters(filterValues),
      });
      setPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Preview failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (selectedColumns.length === 0) {
      toast.error('Pick at least one column.');
      return;
    }
    setExporting(true);
    try {
      const res = await api.post('/report-builder/export', {
        entity: entityKey,
        columns: selectedColumns,
        filters: cleanFilters(filterValues),
      }, { responseType: 'blob' });

      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${entityKey}-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded.');
    } catch (err) {
      toast.error('Export failed.');
    } finally {
      setExporting(false);
    }
  };

  if (!entities) {
    return <div className="loading-page"><div className="spinner" /></div>;
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Report Builder</h1>
          <p className="page-subtitle">Pick what you want to see, filter it, and export to CSV.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={handlePreview} disabled={loading}>
            {loading ? 'Loading…' : '👁 Preview'}
          </button>
          <button className="btn btn-primary" onClick={handleExport} disabled={exporting}>
            {exporting ? 'Exporting…' : '⬇ Export CSV'}
          </button>
        </div>
      </div>

      {/* Entity picker */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><span className="card-title">What do you want to report on?</span></div>
        <div className="card-body" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {Object.entries(entities).map(([key, e]) => (
            <button key={key}
              className={`btn ${entityKey === key ? 'btn-primary' : 'btn-secondary'} btn-sm`}
              onClick={() => switchEntity(key)}>
              {e.label}
            </button>
          ))}
        </div>
      </div>

      {/* Two-column layout: columns + filters */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginBottom: 16 }}>
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span className="card-title">Columns</span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-ghost btn-sm" onClick={selectAll}>Select all</button>
              <button className="btn btn-ghost btn-sm" onClick={selectNone}>Clear</button>
            </div>
          </div>
          <div className="card-body" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
            {(currentEntity?.columns || []).map(col => (
              <label key={col.key} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.875rem' }}>
                <input
                  type="checkbox"
                  checked={selectedColumns.includes(col.key)}
                  onChange={() => toggleColumn(col.key)}
                />
                {col.label}
              </label>
            ))}
          </div>
        </div>

        <div className="card">
          <div className="card-header"><span className="card-title">Filters</span></div>
          <div className="card-body">
            {(!currentEntity?.filters || currentEntity.filters.length === 0) ? (
              <p style={{ color: 'var(--gray-500)', margin: 0, fontSize: '0.875rem' }}>No filters for this entity.</p>
            ) : (
              currentEntity.filters.map(f => (
                <FilterControl key={f.key} filter={f} value={filterValues[f.key]} onChange={v => setFilter(f.key, v)} />
              ))
            )}
          </div>
        </div>
      </div>

      {/* Preview */}
      {preview && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">Preview</span>
            <span style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>
              Showing {preview.rows.length} {preview.rows.length === 1 ? 'row' : 'rows'} (export gives all)
            </span>
          </div>
          <div className="table-wrap" style={{ border: 'none', overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  {preview.columns.map(c => (
                    <th key={c} style={{ whiteSpace: 'nowrap' }}>
                      {c.replace(/_/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase())}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.rows.length === 0 ? (
                  <tr><td colSpan={preview.columns.length}>
                    <div className="empty-state" style={{ padding: 24 }}>
                      <p>No rows match your filters.</p>
                    </div>
                  </td></tr>
                ) : preview.rows.map((r, i) => (
                  <tr key={i}>
                    {preview.columns.map(c => (
                      <td key={c} style={{ fontSize: '0.85rem', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {fmtCell(r[c])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function FilterControl({ filter, value, onChange }) {
  if (filter.type === 'enum') {
    return (
      <div className="form-group">
        <label className="form-label">{filter.label}</label>
        <select className="form-select" value={value || ''} onChange={e => onChange(e.target.value || null)}>
          <option value="">All</option>
          {filter.options.map(o => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
        </select>
      </div>
    );
  }
  if (filter.type === 'bool' || filter.type === 'bool_literal') {
    return (
      <div className="form-group">
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.875rem' }}>
          <input type="checkbox" checked={value === true || value === 'true'} onChange={e => onChange(e.target.checked || null)} />
          {filter.label}
        </label>
      </div>
    );
  }
  if (filter.type === 'date_range') {
    const v = value || {};
    return (
      <div className="form-group">
        <label className="form-label">{filter.label}</label>
        <div style={{ display: 'flex', gap: 6 }}>
          <input type="date" className="form-input" value={v.from || ''}
                 onChange={e => onChange({ ...v, from: e.target.value || undefined })} />
          <input type="date" className="form-input" value={v.to || ''}
                 onChange={e => onChange({ ...v, to: e.target.value || undefined })} />
        </div>
      </div>
    );
  }
  if (filter.type === 'text') {
    return (
      <div className="form-group">
        <label className="form-label">{filter.label}</label>
        <input type="text" className="form-input" value={value || ''}
               placeholder="contains…"
               onChange={e => onChange(e.target.value || null)} />
      </div>
    );
  }
  return null;
}

function cleanFilters(filters) {
  const out = {};
  for (const [k, v] of Object.entries(filters)) {
    if (v === null || v === undefined || v === '') continue;
    if (typeof v === 'object' && !v.from && !v.to) continue;
    out[k] = v;
  }
  return out;
}

function fmtCell(val) {
  if (val == null) return '—';
  if (val === true) return 'Yes';
  if (val === false) return 'No';
  if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(val)) {
    return new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
  if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
    return new Date(val + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
  return String(val);
}
